const SPLIT_ZIP_PART_PATTERN = /^(.+)\.zip\.(\d{3})$/i;
export const MAX_ARCHIVE_IMPORT_SOURCES = 64;
export const MAX_ARCHIVE_IMPORT_SOURCE_BYTES = 500 * 1024 * 1024 * 1024;
export const MAX_ARCHIVE_IMPORT_TOTAL_BYTES = 1024 * 1024 * 1024 * 1024;
export const MAX_ARCHIVE_IMPORT_S3_KEY_BYTES = 1024;
export const ARCHIVE_IMPORT_S3_CASE_ID_RESERVE_BYTES = 120;

const UNSAFE_ARCHIVE_FILE_NAME_PATTERN = /[<>:"/\\|?*]/u;
const ENCODED_PATH_OCTET_PATTERN = /%[0-9A-Fa-f]{2}/u;

const hasAsciiControlCharacter = (value) => Array.from(value).some((character) => {
  const codePoint = character.codePointAt(0);
  return codePoint <= 0x1f || codePoint === 0x7f;
});

const getFileName = (file) => {
  if (typeof file === 'string') return file;
  return typeof file?.name === 'string' ? file.name : '';
};

/**
 * Return the numbered-archive information encoded in a file name.
 *
 * Only the conventional three-digit form is accepted. Similar-looking names
 * such as `delivery.zip.1`, `delivery.zip.001.tmp`, and `delivery.zip.000` are
 * ordinary files and are intentionally left alone.
 */
const parseSplitZipPartCandidate = (fileOrName) => {
  const fileName = getFileName(fileOrName);
  const match = fileName.match(SPLIT_ZIP_PART_PATTERN);
  if (!match) return null;

  const partNumber = Number.parseInt(match[2], 10);
  if (partNumber < 1) return null;

  const archiveName = fileName.slice(0, -4);
  const basename = archiveName.slice(0, -4);

  return {
    archiveName,
    basename,
    fileName,
    normalizedArchiveName: archiveName.toLowerCase(),
    partNumber,
    partSuffix: match[2],
  };
};

const utf8ByteLength = (value) => Array.from(value).reduce((bytes, character) => {
  const codePoint = character.codePointAt(0);
  if (codePoint <= 0x7f) return bytes + 1;
  if (codePoint <= 0x7ff) return bytes + 2;
  if (codePoint <= 0xffff) return bytes + 3;
  return bytes + 4;
}, 0);

const archiveCandidateNameIsServerEligible = (candidate) => {
  if (!candidate || candidate.partNumber > MAX_ARCHIVE_IMPORT_SOURCES) return false;
  const { basename, fileName } = candidate;
  if (
    basename === '.'
    || basename === '..'
    || hasAsciiControlCharacter(fileName)
    || UNSAFE_ARCHIVE_FILE_NAME_PATTERN.test(fileName)
    || ENCODED_PATH_OCTET_PATTERN.test(fileName)
    || utf8ByteLength(fileName) > 255
    || utf8ByteLength(basename) > 255
  ) {
    return false;
  }

  // The API normalizer trims and collapses whitespace. Only divert names that
  // already equal that canonical form so the selected destination preserves
  // each original filename byte for byte.
  const canonicalFileName = fileName.trim().replace(/\s+/gu, ' ').replace(/[ .]+$/u, '');
  const canonicalBaseName = basename.trim().replace(/\s+/gu, ' ').replace(/[ .]+$/u, '');
  return fileName === canonicalFileName && basename === canonicalBaseName;
};

export const parseSplitZipPartName = (fileOrName) => {
  const candidate = parseSplitZipPartCandidate(fileOrName);
  return archiveCandidateNameIsServerEligible(candidate) ? candidate : null;
};

export const archiveImportDestinationIsEligible = (destinationPath) => {
  const rawPath = String(destinationPath || '');
  let path = rawPath.trim();
  if (rawPath !== path) return false;
  if (path === '' || path.toLowerCase() === 'root' || path === '{{Name}}') return true;
  if (
    utf8ByteLength(path) > 1024
    || hasAsciiControlCharacter(path)
    || path.includes('\\')
    || ENCODED_PATH_OCTET_PATTERN.test(path)
    || path.startsWith('/')
    || /^[A-Za-z]:\//u.test(path)
    || /^cases\//iu.test(path)
  ) return false;

  path = path.replace(/\/$/u, '');
  if (path === '{{Name}}') return true;
  if (path.startsWith('{{Name}}/')) path = path.slice('{{Name}}/'.length);
  if (!path || path.includes('//')) return false;

  const parts = path.split('/');
  const eligible = parts.every((part) => {
    if (
      !part
      || part === '.'
      || part === '..'
      || part === '{{Name}}'
      || part.includes('{{')
      || part.includes('}}')
      || /[<>:"|?*]/u.test(part)
      || Array.from(part).length > 180
    ) return false;
    const canonicalPart = part.trim().replace(/\s+/gu, ' ').replace(/[ .]+$/u, '');
    return canonicalPart !== '' && canonicalPart === part;
  });
  return eligible && utf8ByteLength(`{{Name}}/${parts.join('/')}`) <= 1024;
};

const canonicalArchiveImportDestination = (destinationPath) => {
  const path = String(destinationPath || '').replace(/\/$/u, '');
  if (path === '' || path.toLowerCase() === 'root' || path === '{{Name}}') return '{{Name}}';
  return path.startsWith('{{Name}}/') ? path : `{{Name}}/${path}`;
};

export const archiveImportGroupFitsDestination = (group, destinationPath) => {
  if (!archiveImportDestinationIsEligible(destinationPath) || !group) return false;
  const destination = canonicalArchiveImportDestination(destinationPath);
  const exactFileNames = Array.from(group.exactFileNames || []);
  const extractionFolderName = String(group.extractionFolderName || exactFileNames[0] || '');
  if (!extractionFolderName || exactFileNames.length === 0) return false;

  const destinationPrefixBytes = utf8ByteLength('cases/')
    + ARCHIVE_IMPORT_S3_CASE_ID_RESERVE_BYTES
    + utf8ByteLength('/')
    + utf8ByteLength(destination)
    + utf8ByteLength('/');
  const extractionFolderBytes = utf8ByteLength(extractionFolderName);

  // Originals remain ordinary files in the selected folder. Preparation uses
  // the exact first numbered filename as its sibling prefix and mirrors the
  // API's concrete extraction-root probe.
  const originalsFit = exactFileNames.every((name) => (
    destinationPrefixBytes + utf8ByteLength(String(name || '')) <= MAX_ARCHIVE_IMPORT_S3_KEY_BYTES
  ));
  const extractionFolderFits = destinationPrefixBytes
    + extractionFolderBytes
    + utf8ByteLength('/check')
    <= MAX_ARCHIVE_IMPORT_S3_KEY_BYTES;
  return originalsFit && extractionFolderFits;
};

/**
 * Keep an overlong package in the ordinary case-upload path. This mirrors the
 * API preflight and prevents the archive chooser from taking ownership of a
 * selection whose exact originals cannot fit beneath the selected folder.
 */
export const constrainArchiveDetectionToDestination = (detection, destinationPath) => {
  const capturedDestinationPath = canonicalArchiveImportDestination(destinationPath);
  const groups = Array.from(detection?.groups || []);
  const fittingGroups = groups.filter((group) => archiveImportGroupFitsDestination(group, destinationPath));
  const fittingIds = new Set(fittingGroups.map(({ id }) => id));
  const overflowGroups = groups
    .filter(({ id }) => !fittingIds.has(id))
    .map((group) => ({ ...group, unsupportedReason: 'destination_path_too_long' }));
  const ordinaryFiles = new Set(detection?.unrelatedFiles || []);
  overflowGroups.forEach((group) => group.files.forEach((file) => ordinaryFiles.add(file)));

  return {
    ...detection,
    destinationPath: capturedDestinationPath,
    groups: fittingGroups,
    hasSplitArchives: fittingGroups.length > 0,
    incompleteGroups: Array.from(detection?.incompleteGroups || [])
      .filter(({ id }) => fittingIds.has(id)),
    unrelatedFiles: Array.from(detection?.selectedFiles || [])
      .filter((file) => ordinaryFiles.has(file)),
    unsupportedGroups: [
      ...Array.from(detection?.unsupportedGroups || []),
      ...overflowGroups,
    ],
    validGroups: Array.from(detection?.validGroups || [])
      .filter(({ id }) => fittingIds.has(id)),
  };
};

const sortParts = (left, right) => (
  left.partNumber - right.partNumber
  || left.fileName.localeCompare(right.fileName)
);

const buildGroup = (pendingGroup) => {
  const parts = [...pendingGroup.parts].sort(sortParts);
  const partCounts = new Map();

  parts.forEach(({ partNumber }) => {
    partCounts.set(partNumber, (partCounts.get(partNumber) || 0) + 1);
  });

  const partNumbers = [...partCounts.keys()].sort((left, right) => left - right);
  const highestPartNumber = partNumbers[partNumbers.length - 1] || 0;
  const missingPartNumbers = [];

  for (let partNumber = 1; partNumber <= highestPartNumber; partNumber += 1) {
    if (!partCounts.has(partNumber)) missingPartNumbers.push(partNumber);
  }

  // A numbered ZIP's final part cannot be inferred from `.001` alone. Treat
  // that selection as incomplete so preparation cannot start until the user
  // also selects `.002` (and any later parts). The files can still follow the
  // archive-only path when the user intentionally wants to keep just `.001`.
  if (parts.length === 1 && highestPartNumber === 1) {
    missingPartNumbers.push(2);
  }

  const duplicatePartNumbers = partNumbers.filter((partNumber) => partCounts.get(partNumber) > 1);
  const isComplete = missingPartNumbers.length === 0 && duplicatePartNumbers.length === 0;
  const totalBytes = parts.reduce((sum, { file }) => sum + Number(file?.size || 0), 0);
  const firstPart = parts.find(({ partNumber }) => partNumber === 1);

  return {
    archiveName: pendingGroup.archiveName,
    basename: pendingGroup.basename,
    duplicatePartNumbers,
    exactFileNames: parts.map(({ fileName }) => fileName),
    // A split ZIP is one logical archive. Its extracted contents therefore
    // get one sibling folder, named after the exact part-one filename rather
    // than whichever numbered chunk the browser happened to receive first.
    extractionFolderName: firstPart?.fileName || '',
    files: parts.map(({ file }) => file),
    highestPartNumber,
    id: pendingGroup.id,
    isComplete,
    missingPartNumbers,
    partNumbers,
    parts,
    totalBytes,
  };
};

/**
 * Separate selected files into complete numbered ZIP deliveries, incomplete
 * deliveries, and files that are unrelated to a numbered ZIP delivery.
 * Original File references are retained so callers can pass them directly to
 * the existing upload flow.
 */
export const detectSplitZipArchives = (selectedFiles) => {
  const files = Array.from(selectedFiles || []);
  const pendingGroups = new Map();
  const candidateGroupByFile = new Map();

  files.forEach((file) => {
    const parsed = parseSplitZipPartCandidate(file);
    if (!parsed) {
      return;
    }

    const groupId = parsed.normalizedArchiveName;
    if (!pendingGroups.has(groupId)) {
      pendingGroups.set(groupId, {
        archiveName: parsed.archiveName,
        basename: parsed.basename,
        id: groupId,
        parts: [],
      });
    }

    pendingGroups.get(groupId).parts.push({
      eligible: archiveCandidateNameIsServerEligible(parsed)
        && Number.isFinite(Number(file?.size))
        && Number(file.size) > 0
        && Number(file.size) <= MAX_ARCHIVE_IMPORT_SOURCE_BYTES,
      file,
      fileName: parsed.fileName,
      partNumber: parsed.partNumber,
      partSuffix: parsed.partSuffix,
    });
    candidateGroupByFile.set(file, groupId);
  });

  const candidateGroups = [...pendingGroups.values()].map(buildGroup);
  const unsupportedGroups = candidateGroups.filter((group) => (
    group.files.length > MAX_ARCHIVE_IMPORT_SOURCES
    || group.totalBytes > MAX_ARCHIVE_IMPORT_TOTAL_BYTES
    || group.parts.some((part) => !part.eligible)
  ));
  const unsupportedGroupIds = new Set(unsupportedGroups.map(({ id }) => id));
  const groups = candidateGroups.filter(({ id }) => !unsupportedGroupIds.has(id));
  const validGroups = groups.filter(({ isComplete }) => isComplete);
  const incompleteGroups = groups.filter(({ isComplete }) => !isComplete);
  const unrelatedFiles = files.filter((file) => {
    const groupId = candidateGroupByFile.get(file);
    return groupId === undefined || unsupportedGroupIds.has(groupId);
  });

  return {
    groups,
    hasSplitArchives: groups.length > 0,
    incompleteGroups,
    selectedFiles: files,
    unrelatedFiles,
    unsupportedGroups,
    validGroups,
  };
};

export default detectSplitZipArchives;
